// The `commerce` family: where work and money ENTER (#4435, epic #4391).
//
// Mirrors `../../audit/kind-commerce.ts` exactly — every kind of it: the raw-task
// admission trail, the donation lifecycle, entitlement grants, a goal rename and
// the API-token lifecycle. Every kind here RENDERS; this is the smallest family
// and, per the vocabulary's own doc comments, almost entirely a record of
// operator/user-facing acts rather than internal bookkeeping, so `'omit'` would
// be throwing away exactly the lines an operator scans this console for.
//
// ─── WHY THE PAYLOAD READS ARE DEFENSIVE ─────────────────────────────────────
//
// `TerminalRow.input`/`.output` are `unknown` and are NULLED outright when
// `visibleToMaintainer` is false (`wire/terminal.ts` § the row a renderer
// reads). Several writers in this family set that flag false (the raw-task
// triage announce, for one), so every field below is read through a narrowing
// helper that answers `undefined` rather than throwing or lying about a shape
// that was withheld. A renderer that assumed the payload's shape from the
// writer alone would crash the console the day that flag is true.

import type {
  TerminalRefs,
  TerminalRenderContext,
  TerminalRendered,
  TerminalRow,
  TerminalSubject,
  TerminalSubjectKind,
} from '../../../wire/terminal';
import { terminalRendererGroup } from '../../../wire/terminal';
import { ACCOUNT_VERBS_COMMERCE } from './account-verb';
import { TERMINAL_RENDERERS_BLOCKER_GUIDANCE } from './commerce-blocker-guidance';
import { TERMINAL_RENDERERS_WEBHOOK_SOURCE_HEALTH } from './commerce-webhook-source';

// ─── payload narrowing (no writer's shape is trusted blind) ──────────────────

function asRecord(v: unknown): Record<string, unknown> | null {
  return typeof v === 'object' && v !== null ? (v as Record<string, unknown>) : null;
}

function str(o: Record<string, unknown> | null, key: string): string | undefined {
  const v = o?.[key];
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

/** A field that may have been minted as a number OR a string id — never coerced. */
function idLike(o: Record<string, unknown> | null, key: string): string | undefined {
  const v = o?.[key];
  if (typeof v === 'string' && v.length > 0) return v;
  if (typeof v === 'number') return String(v);
  return undefined;
}

function strArray(o: Record<string, unknown> | null, key: string): string[] | undefined {
  const v = o?.[key];
  return Array.isArray(v) && v.every((x) => typeof x === 'string') ? (v as string[]) : undefined;
}

function bool(o: Record<string, unknown> | null, key: string): boolean | undefined {
  const v = o?.[key];
  return typeof v === 'boolean' ? v : undefined;
}

/** `string | number` straight off a payload field — line params admit nothing else. */
function scalarParam(o: Record<string, unknown> | null, key: string): string | number | undefined {
  const v = o?.[key];
  return typeof v === 'string' || typeof v === 'number' ? v : undefined;
}

/** The row's own `repoFullName` column, never gated by `visibleToMaintainer`. */
function repoRefs(row: TerminalRow): TerminalRefs {
  return row.repoFullName !== null ? { repoFullName: row.repoFullName } : {};
}

/**
 * `{ kind, id }` when the row names a real id of `kind`; the session subject
 * otherwise (`wire/terminal.ts` § `TerminalSubject`). The audit row's own uuid
 * is never an `id` — it is not a task, decision, issue or key id, and a console
 * grouping by it would file one line per row under a subject nothing else
 * shares (#4445). `label` stays the display handle either way.
 */
function subjectOrSession(
  ctx: TerminalRenderContext,
  kind: TerminalSubjectKind,
  id: string | undefined,
  label: string,
): TerminalSubject {
  if (id !== undefined) return { kind, id, label };
  return { kind: 'session', id: ctx.row.sessionId, label };
}

// ─── raw_task.* — the pre-triage intake lane ─────────────────────────────────

/**
 * Shared shape for the three SETTLE kinds (`triaged`/`dropped`/`escalated`):
 * one row per terminal `RawTaskStatus`, all three read the same two fields off
 * the announce row (`raw-task-triage.ts`, `raw-task-bridge.ts`) — the raw
 * task's own id (`input.rawTaskId`) and the verdict's `reason` (`output.reason`).
 * A row settled by the maintainer-invisible triage lane carries neither, and
 * falls back to the session subject (`wire/terminal.ts` § `TerminalSubject`)
 * rather than inventing a raw-task id that was never on this row.
 */
function rawTaskSettleRendered(
  ctx: TerminalRenderContext,
  key: `terminal.raw_task.${string}`,
): TerminalRendered {
  const input = asRecord(ctx.row.input);
  const output = asRecord(ctx.row.output);
  const rawTaskId = idLike(input, 'rawTaskId');
  const reason = str(output, 'reason');
  return {
    line: { key, params: reason !== undefined ? { reason } : {} },
    subject: subjectOrSession(ctx, 'task', rawTaskId, rawTaskId ?? 'raw task'),
    refs: rawTaskId !== undefined ? { taskId: rawTaskId } : {},
  };
}

/**
 * `raw_task.held`: the settle shape, except that a `cause` outranks the stored
 * `reason` (plan 102 slice 05). A hold whose key EXISTS but is cooling is stored
 * `reason: 'key_cooling'` since A4b of #6501 (migration 0342), and was stored
 * `reason: 'no_byok'` before it; either way its row carries `cause: 'key_cooling'` plus
 * `retryAt`, and the line says what holds the ask and until when, never "no key".
 * Both spellings read the same because the cause outranks the reason, and the
 * `no_byok` rows stay: audit rows are never rewritten. It is HELD ONLY: the three hold steps in
 * `apps/agents-manager/src/triage-hold.ts` — `holdOnTenant` (`cause: 'key_cooling'`),
 * `holdOnModel` (a `ModelHoldCause`, #5110/#5114), and `holdOnSkipSet`
 * (`cause: 'batch_key_failed_this_ask'`, #5112) — are the only producers of a
 * `cause` on any `raw_task.*` row, and the settle kinds must keep their verdict's reason.
 */
function rawTaskHeldRendered(ctx: TerminalRenderContext): TerminalRendered {
  const rendered = rawTaskSettleRendered(ctx, 'terminal.raw_task.held');
  const output = asRecord(ctx.row.output);
  const cause = str(output, 'cause');
  if (cause === undefined) return rendered;
  const until = str(output, 'retryAt');
  return {
    ...rendered,
    line: {
      ...rendered.line,
      params: { reason: cause, ...(until !== undefined ? { until } : {}) },
    },
  };
}

/**
 * `raw_task.deferred` (#5387): the PR it waits on and the repo, read defensively off
 * `output` (`pr_number` may arrive as a number or a string id; neither is coerced).
 */
function rawTaskDeferredRendered(ctx: TerminalRenderContext): TerminalRendered {
  const input = asRecord(ctx.row.input);
  const output = asRecord(ctx.row.output);
  const rawTaskId = idLike(input, 'rawTaskId');
  const pr = scalarParam(output, 'pr_number') ?? '?';
  const repo = str(output, 'repo') ?? ctx.row.repoFullName ?? 'unknown repo';
  return {
    line: { key: 'terminal.raw_task.deferred', params: { pr, repo } },
    subject: subjectOrSession(ctx, 'task', rawTaskId, rawTaskId ?? 'raw task'),
    refs: { ...(rawTaskId !== undefined ? { taskId: rawTaskId } : {}), ...repoRefs(ctx.row) },
  };
}

/**
 * `raw_task.screened` (H3 #5393): the screen's answer and, on a discard, its closed
 * reason. `cached` says whether a stored verdict answered it (no model call). A
 * withheld row reads as `screened: unknown` on the session, never a made-up verdict.
 */
function rawTaskScreenedRendered(ctx: TerminalRenderContext): TerminalRendered {
  const input = asRecord(ctx.row.input);
  const output = asRecord(ctx.row.output);
  const rawTaskId = idLike(input, 'rawTaskId');
  const verdict = str(output, 'verdict') ?? 'unknown';
  const reason = str(output, 'reason') ?? 'none';
  const cached = bool(output, 'cached');
  return {
    line: {
      key: 'terminal.raw_task.screened',
      params: { verdict, reason, source: cached === true ? 'stored' : 'model' },
    },
    subject: subjectOrSession(ctx, 'task', rawTaskId, rawTaskId ?? 'raw task'),
    refs: rawTaskId !== undefined ? { taskId: rawTaskId } : {},
  };
}

/**
 * `raw_task.claim` (H6 #5420): whether triage found the report true, whose it was, the
 * verdict the floor left (not the final one) and what the trust floor did. A row missing a field reads
 * `unknown`, never a made-up answer.
 */
function rawTaskClaimRendered(ctx: TerminalRenderContext): TerminalRendered {
  const input = asRecord(ctx.row.input);
  const output = asRecord(ctx.row.output);
  const rawTaskId = idLike(input, 'rawTaskId');
  return {
    line: {
      key: 'terminal.raw_task.claim',
      params: {
        claim: str(output, 'claim') ?? 'unknown',
        trust: str(output, 'trust') ?? 'unknown',
        floor_verdict: str(output, 'floor_verdict') ?? 'unknown',
        floor: str(output, 'floor') ?? 'unknown',
      },
    },
    subject: subjectOrSession(ctx, 'task', rawTaskId, rawTaskId ?? 'raw task'),
    refs: rawTaskId !== undefined ? { taskId: rawTaskId } : {},
  };
}

/**
 * `raw_task.escalation_delivered` (#7649): what the escalate's notice did after its
 * settle — `queued` or `undelivered`, read off `output.delivery`; `unknown` when a row
 * carries neither.
 */
function rawTaskEscalationDeliveredRendered(ctx: TerminalRenderContext): TerminalRendered {
  const input = asRecord(ctx.row.input);
  const rawTaskId = idLike(input, 'rawTaskId');
  return {
    line: {
      key: 'terminal.raw_task.escalation_delivered',
      params: { delivery: str(asRecord(ctx.row.output), 'delivery') ?? 'unknown' },
    },
    subject: subjectOrSession(ctx, 'task', rawTaskId, rawTaskId ?? 'raw task'),
    refs: rawTaskId !== undefined ? { taskId: rawTaskId } : {},
  };
}

/**
 * `raw_task.amended` (#7082): a scheduled checkup's second subject on a brief row was
 * appended to the row's still-unclaimed ask. Names the subject it added; anchored on the
 * ask when the row names it, the session otherwise. A withheld row reads as `unknown`.
 */
function rawTaskAmendedRendered(ctx: TerminalRenderContext): TerminalRendered {
  const input = asRecord(ctx.row.input);
  const rawTaskId = idLike(input, 'rawTaskId');
  return {
    line: {
      key: 'terminal.raw_task.amended',
      params: { anchor: str(input, 'anchor') ?? 'unknown' },
    },
    subject: subjectOrSession(ctx, 'task', rawTaskId, rawTaskId ?? 'raw task'),
    refs: rawTaskId !== undefined ? { taskId: rawTaskId } : {},
  };
}

/**
 * `prompt.item_screened` (H5 #5403): the screen's answer for ONE third-party item a
 * working agent was about to be handed — which field it was, and whether it passed,
 * was discarded (with its closed reason) or was withheld because the screen could not
 * answer. Anchored on the task when the row names one, the session otherwise. A
 * withheld row reads as `unknown`, never a made-up verdict.
 */
function promptItemScreenedRendered(ctx: TerminalRenderContext): TerminalRendered {
  const input = asRecord(ctx.row.input);
  const output = asRecord(ctx.row.output);
  const taskId = idLike(input, 'task_id');
  const item = str(input, 'item_kind') ?? 'unknown';
  const verdict = str(output, 'verdict') ?? 'unknown';
  const reason = str(output, 'reason') ?? 'none';
  const cached = bool(output, 'cached');
  return {
    line: {
      key: 'terminal.prompt.item_screened',
      params: { item, verdict, reason, source: cached === true ? 'stored' : 'model' },
    },
    subject: subjectOrSession(ctx, 'task', taskId, taskId ?? 'screened item'),
    refs: taskId !== undefined ? { taskId } : {},
  };
}

// ─── donation.* / repo.donations_* — subject + refs shared shape ─────────────

/**
 * Every `donation.*` kind is keyed to the DONOR's account
 * (`entities/audit/kind-commerce.ts` § arc 101 slice 12) but a gift pinned to a
 * project is what an operator actually recognises — `row.repoFullName` is set
 * on those rows, so a repo-pinned gift reads as a `repo` subject and an
 * account-wide one (compute/LLM handed to an org rather than a project) reads
 * as `account`. `donationId` is the one field every one of the six kinds
 * carries somewhere in its payload (`output` on the mint, `input` everywhere
 * else); it rides in `refs`/params, never `id` — `id` must be a real id of
 * `kind` (`wire/terminal.ts`'s `TerminalSubject` doc), and a donation id is
 * neither a repo id nor an account id. Round-2 review caught this computing
 * `kind` and `id` from disjoint sources: `kind:'repo'` with a DONATION id as
 * `id`, unconditionally — a repo-pinned gift never actually grouped by repo.
 */
function donationSubject(ctx: TerminalRenderContext): TerminalSubject {
  const input = asRecord(ctx.row.input);
  const targetAccountId = idLike(input, 'targetAccountId') ?? idLike(input, 'donorAccountId');
  if (ctx.row.repoFullName !== null) {
    return { kind: 'repo', id: ctx.row.repoFullName, label: ctx.row.repoFullName };
  }
  if (targetAccountId !== undefined) {
    return { kind: 'account', id: targetAccountId, label: targetAccountId };
  }
  return { kind: 'session', id: ctx.row.sessionId, label: ctx.row.sessionId };
}

function repoDonationsRendered(
  ctx: TerminalRenderContext,
  key: `terminal.repo.${string}`,
): TerminalRendered {
  const input = asRecord(ctx.row.input);
  const via = str(input, 'via') ?? 'unknown';
  // `id` is the row's own repo full name, never `repoId` (a different id space —
  // the writer's internal repo row uuid) and never the literal fallback string
  // `'repo'` the previous version could emit as an `id` (round-2 review).
  const subject: TerminalSubject =
    ctx.row.repoFullName !== null
      ? { kind: 'repo', id: ctx.row.repoFullName, label: ctx.row.repoFullName }
      : { kind: 'session', id: ctx.row.sessionId, label: ctx.row.sessionId };
  return {
    line: { key, params: { via } },
    subject,
    refs: repoRefs(ctx.row),
  };
}

// ─── grant.* — the subject is the POLICY OVERRIDE, not the target ────────────

/**
 * A grant is an audited entitlement override — the same reason
 * `blocker.secrets_landed` is a `decision` subject rather than whatever it
 * happens to be about: the row an operator scans for is "who authorized free
 * capacity", not the account it landed on. `subjectId` (the account or user the
 * grant LIFTS) is the more recognisable display handle, so it leads the label;
 * `grantId` is the durable identity and the ONLY `id` — an account id is not a
 * decision id, so a row without a grant id falls back to the session subject.
 */
function grantSubject(ctx: TerminalRenderContext): TerminalSubject {
  const input = asRecord(ctx.row.input);
  const output = asRecord(ctx.row.output);
  const grantId = idLike(output, 'grantId') ?? idLike(input, 'grantId');
  const subjectId = idLike(input, 'subjectId');
  return subjectOrSession(ctx, 'decision', grantId, subjectId ?? grantId ?? 'grant');
}

// ─── the table ────────────────────────────────────────────────────────────────

export const TERMINAL_RENDERERS_COMMERCE = terminalRendererGroup({
  // Written ONLY for a genuinely new raw task (a redelivery is not a state
  // change), by the one builder every inlet files through: `@developerz/audit`
  // `rawTaskCreatedDraft` (the raw-task door and the blocker answer). `system`:
  // every inlet is a queue/webhook/API door, never a person in the console.
  //
  // TWO KEY SETS, because `audit_events` is append-only and both live forever: the
  // builder's `rawTaskId` / `repoFullNames`, and the snake_case `raw_task_id` /
  // `repos` earlier api and blocker rows carry (#4770). Reading one would render
  // the other's rows as "raw task, repos: none".
  'raw_task.created': {
    level: 'info',
    actor: 'system',
    render: (ctx) => {
      const input = asRecord(ctx.row.input);
      const rawTaskId = idLike(input, 'rawTaskId') ?? idLike(input, 'raw_task_id');
      const source = str(input, 'source') ?? 'unknown';
      const repos = strArray(input, 'repoFullNames') ?? strArray(input, 'repos');
      return {
        line: {
          key: 'terminal.raw_task.created',
          params: { source, repos: repos !== undefined ? repos.join(', ') : 'none' },
        },
        subject: subjectOrSession(ctx, 'task', rawTaskId, rawTaskId ?? 'raw task'),
        refs: rawTaskId !== undefined ? { taskId: rawTaskId } : {},
      };
    },
  },
  // `packages/domain/src/entities/raw-task/row.ts` — the `schedule` verdict:
  // the ask was admitted as real work. A positive routine outcome.
  'raw_task.triaged': {
    level: 'ok',
    actor: 'system',
    render: (ctx) => rawTaskSettleRendered(ctx, 'terminal.raw_task.triaged'),
  },
  // The `drop-noise` / `dupe-of` verdicts: decided not to happen. Routine, not
  // a failure — the whole point of triage is that most asks end here.
  'raw_task.dropped': {
    level: 'info',
    actor: 'system',
    render: (ctx) => rawTaskSettleRendered(ctx, 'terminal.raw_task.dropped'),
  },
  // The `escalate` verdict: "a human decides" (`RawTaskStatus` doc). `decision`
  // is exactly the level `TerminalLevel` names for this — a row an operator
  // must not read as ordinary background noise.
  'raw_task.escalated': {
    level: 'decision',
    actor: 'system',
    render: (ctx) => rawTaskSettleRendered(ctx, 'terminal.raw_task.escalated'),
  },
  // #7649: the escalate's notice after its settle. `info` for both outcomes: the
  // `decision` level was the settle row's, and an `undelivered` one is named on the
  // line itself, so this row owes nobody a second act.
  'raw_task.escalation_delivered': {
    level: 'info',
    actor: 'system',
    render: (ctx) => rawTaskEscalationDeliveredRendered(ctx),
  },
  // The `needs_info` verdict (plan 101 slice 08): the ask is parked on its ASKER.
  // `decision` for the reason `raw_task.escalated` is — a person owes this row an
  // act, here the reporter's answer, and it must not read as background noise.
  'raw_task.awaiting_info': {
    level: 'decision',
    actor: 'system',
    render: (ctx) => rawTaskSettleRendered(ctx, 'terminal.raw_task.awaiting_info'),
  },
  // Plan 101 L1: a recoverable tenant gate HELD the ask, unsettled and uncharged.
  // `warn`, not `decision`: the account owes a fix (fund it, verify a key), but the ask
  // resumes on its own once that signal lands. Nobody has to act on THIS row.
  'raw_task.held': {
    level: 'warn',
    actor: 'system',
    render: (ctx) => rawTaskHeldRendered(ctx),
  },
  // #5387: triage deferred the ask behind a person's open PR. `info`: nobody owes this
  // row an act — the ask is re-judged on its own once the hold lapses.
  'raw_task.deferred': {
    level: 'info',
    actor: 'system',
    render: (ctx) => rawTaskDeferredRendered(ctx),
  },
  // H3 #5393: the intake screen read the ask. `info` for both answers: a discard is
  // SETTLED by the same act (its `raw_task.dropped` row), so nobody owes this row an act.
  'raw_task.screened': {
    level: 'info',
    actor: 'system',
    render: (ctx) => rawTaskScreenedRendered(ctx),
  },
  // H6 #5420: triage graded whether the report is true. `info`: the verdict it ended
  // with is settled (and, when it needs a person, announced) by its own row.
  'raw_task.claim': {
    level: 'info',
    actor: 'system',
    render: (ctx) => rawTaskClaimRendered(ctx),
  },
  // #7082: an unclaimed ask gained a second subject. `info`: it is judged with its new
  // text like any pending ask, so nobody owes this row an act.
  'raw_task.amended': {
    level: 'info',
    actor: 'system',
    render: (ctx) => rawTaskAmendedRendered(ctx),
  },
  // H5 #5403: the screen read one push-path item before a working agent saw it. `info`
  // for every answer: a withheld item is replaced by its placeholder in the same act,
  // so nobody owes this row an act.
  'prompt.item_screened': {
    level: 'info',
    actor: 'system',
    render: (ctx) => promptItemScreenedRendered(ctx),
  },
  // `apps/worker/src/wiring/nats-consumers.ts`'s `ingestBlocker` — a blocker
  // RAISED (#4400, Terminal slice 09). `decision` subject: the row an operator
  // scans for is which ask is now open, not the raising run's housekeeping
  // fields. Fields are camelCase because this row's ONE writer already shaped
  // them that way (`blockerId`/`blockerKind`), unlike `blocker.secrets_landed`
  // below — a renderer reads what its writer actually sends, never a house
  // style the writer never adopted. `system`: no human raises a blocker, a run
  // does. No `blockerId` -> no real id of `kind: 'decision'` -> the session
  // fallback (#4445's row-id class, never the audit row's own uuid), same
  // contract `installation.linked`/`.revoked` already use for the identical gap.
  'blocker.asked': {
    level: 'decision',
    actor: 'system',
    render: (ctx) => {
      const input = asRecord(ctx.row.input);
      const blockerId = idLike(input, 'blockerId');
      const blockerKind = str(input, 'blockerKind') ?? 'decision_needed';
      const source = str(input, 'source') ?? 'unknown';
      return {
        line: {
          key: 'terminal.blocker.asked',
          params: { kind: blockerKind, source },
        },
        subject: subjectOrSession(ctx, 'decision', blockerId, blockerId ?? 'blocker'),
        refs: {
          ...(blockerId !== undefined ? { blockerId } : {}),
          ...repoRefs(ctx.row),
        },
      };
    },
  },
  // `apps/api/src/deps/blockers.ts` — a blocker ANSWERED, through either door
  // (the prose `answer` or a completed form's close in `blocker-submit.ts`)
  // (#4400, Terminal slice 09). `decision` subject, matching `blocker.asked`:
  // the row an operator scans for is which ask just closed. `human` is the
  // declared default — a form completed through the fix link carries no
  // session user either, and `project.ts` only ever CORRECTS toward `human`
  // when the row has one, never away from it. No `blocker_id` -> the same
  // session fallback `blocker.asked` uses for the identical gap.
  'blocker.answered': {
    level: 'ok',
    actor: 'human',
    render: (ctx) => {
      const input = asRecord(ctx.row.input);
      const output = asRecord(ctx.row.output);
      const blockerId = idLike(input, 'blocker_id');
      const blockerKind = str(input, 'blocker_kind') ?? 'decision_needed';
      const rawTaskId = idLike(output, 'raw_task_id');
      return {
        line: {
          key: 'terminal.blocker.answered',
          params: { kind: blockerKind },
        },
        subject: subjectOrSession(ctx, 'decision', blockerId, blockerId ?? 'blocker'),
        refs: {
          ...(blockerId !== undefined ? { blockerId } : {}),
          ...(rawTaskId !== undefined ? { taskId: rawTaskId } : {}),
          ...repoRefs(ctx.row),
        },
      };
    },
  },
  // A caller withdrew the blocker (`apps/api/src/deps/blockers.ts`, either door).
  // `system`, not `human`: a machine credential may withdraw, and
  // `projectTerminalEvent` already corrects the actor to `human` when the row
  // carries a user id.
  'blocker.withdrawn': {
    level: 'info',
    actor: 'system',
    render: (ctx) => {
      const input = asRecord(ctx.row.input);
      const blockerId = idLike(input, 'blocker_id');
      return {
        line: {
          key: 'terminal.blocker.withdrawn',
          params: { kind: str(input, 'blocker_kind') ?? 'decision_needed' },
        },
        subject: subjectOrSession(ctx, 'decision', blockerId, blockerId ?? 'blocker'),
        refs: {
          ...(blockerId !== undefined ? { blockerId } : {}),
          ...repoRefs(ctx.row),
        },
      };
    },
  },
  // `apps/api/src/deps/blocker-submit.ts` — access landed on a blocker's form.
  // `decision` subject (like a grant): the row an operator scans for is which
  // access landed, not the blocker's own housekeeping fields. Usually a human
  // filling a form through the fix link; `human` is the declared default and
  // `project.ts` corrects it when the row's own `userId` says otherwise.
  'blocker.secrets_landed': {
    level: 'ok',
    actor: 'human',
    render: (ctx) => {
      const input = asRecord(ctx.row.input);
      const output = asRecord(ctx.row.output);
      const blockerId = idLike(input, 'blocker_id');
      const names = strArray(input, 'names');
      const complete = bool(output, 'complete');
      return {
        line: {
          key: 'terminal.blocker.secrets_landed',
          params: {
            names: names !== undefined && names.length > 0 ? names.join(', ') : 'none',
            complete: complete === true ? 'yes' : 'no',
          },
        },
        subject: subjectOrSession(ctx, 'decision', blockerId, blockerId ?? 'blocker'),
        refs: {
          ...(blockerId !== undefined ? { blockerId } : {}),
          ...repoRefs(ctx.row),
        },
      };
    },
  },
  // `apps/api/src/deps/repos.ts` — the maintainer's own consent toggle for
  // receiving donated boxes/keys. Owner-authenticated by construction, so
  // `human` (never a background job flips this column).
  'repo.donations_enabled': {
    level: 'info',
    actor: 'human',
    render: (ctx) => repoDonationsRendered(ctx, 'terminal.repo.donations_enabled'),
  },
  'repo.donations_disabled': {
    level: 'info',
    actor: 'human',
    render: (ctx) => repoDonationsRendered(ctx, 'terminal.repo.donations_disabled'),
  },
  // `apps/api/src/deps/donations.ts` — a gift recorded. `ok`: a new gift is a
  // positive outcome, and for a `money` gift it may still be pending payment —
  // that nuance rides `donation.activated` below, not a second level here.
  'donation.created': {
    level: 'ok',
    actor: 'human',
    render: (ctx) => {
      const input = asRecord(ctx.row.input);
      const donationKind = str(input, 'donationKind') ?? 'unknown';
      return {
        line: { key: 'terminal.donation.created', params: { donationKind } },
        subject: donationSubject(ctx),
        refs: repoRefs(ctx.row),
      };
    },
  },
  // Either the donor's own retraction, or a best-effort cleanup after a failed
  // checkout mint (`reason: 'checkout_mint_failed*'`). `info`: a detach is
  // routine even on the failure branch — the row that actually failed is the
  // Stripe call, not this trail entry.
  //
  // `actor: 'system'`, not `'human'`: measured at BOTH writer arms
  // (`apps/api/src/deps/donations.ts`'s checkout-mint-failure cleanup and the
  // donor's own `cancel`) — `grep -n userId apps/api/src/deps/donations.ts`
  // returns nothing, so NEITHER passes a `userId` to `audit(...)`, and
  // `project.ts`'s actor override never fires on either. An earlier version of
  // this comment claimed the donor's own retraction DOES carry a userId; it does
  // not (round-2 review caught the claim, not just the declared actor — the
  // wrong actor was already fixed, the docblock defending it was not). A
  // donor's deliberate retraction rendering as a system act is a real, known
  // gap; its fix is threading the principal at the writer, not here.
  'donation.cancelled': {
    level: 'info',
    actor: 'system',
    render: (ctx) => {
      const input = asRecord(ctx.row.input);
      const donationKind = str(input, 'donationKind');
      const reason = str(input, 'reason');
      return {
        line: {
          key: 'terminal.donation.cancelled',
          params: {
            ...(donationKind !== undefined ? { donationKind } : {}),
            ...(reason !== undefined ? { reason } : {}),
          },
        },
        subject: donationSubject(ctx),
        refs: repoRefs(ctx.row),
      };
    },
  },
  // The recipient refused the gift. Keyed to the recipient's account
  // (`donorAccountId` rides in the payload for the other party), a refusal
  // that changes nothing else — `info`.
  'donation.declined': {
    level: 'info',
    actor: 'human',
    render: (ctx) => {
      const input = asRecord(ctx.row.input);
      const donationKind = str(input, 'donationKind') ?? 'unknown';
      return {
        line: { key: 'terminal.donation.declined', params: { donationKind } },
        subject: donationSubject(ctx),
        refs: repoRefs(ctx.row),
      };
    },
  },
  // The recipient pointed a donated LLM key at a model inside the donor's
  // ceiling. `output.chosenModel` is present-and-null when the pick was
  // cleared — rendered as the literal `cleared`, not folded into "unknown".
  'donation.model_chosen': {
    level: 'info',
    actor: 'human',
    render: (ctx) => {
      const output = asRecord(ctx.row.output);
      const chosenRaw = output?.['chosenModel'];
      const chosenModel = typeof chosenRaw === 'string' ? chosenRaw : 'cleared';
      return {
        line: { key: 'terminal.donation.model_chosen', params: { chosenModel } },
        subject: donationSubject(ctx),
        refs: repoRefs(ctx.row),
      };
    },
  },
  // `apps/worker/src/consumers/stripe-events.lane.ts` — the webhook flip that
  // puts a `money` gift on the backer wall. No human acted in this hop; Stripe
  // did. `ok`: the gift now counts.
  'donation.activated': {
    level: 'ok',
    actor: 'system',
    render: (ctx) => {
      const input = asRecord(ctx.row.input);
      const subscriptionId = str(input, 'subscriptionId');
      return {
        line: {
          key: 'terminal.donation.activated',
          params: subscriptionId !== undefined ? { subscriptionId } : {},
        },
        subject: donationSubject(ctx),
        refs: repoRefs(ctx.row),
      };
    },
  },
  // The donor's subscription ended, so every gift it funded PARKED — resources
  // stopped operating. `warn`: an operational impact worth flagging, unlike a
  // voluntary cancel.
  'donation.parked': {
    level: 'warn',
    actor: 'system',
    render: (ctx) => {
      const input = asRecord(ctx.row.input);
      const output = asRecord(ctx.row.output);
      const reason = str(input, 'reason') ?? 'unknown';
      const parkedCount = scalarParam(output, 'parked_count');
      return {
        line: {
          key: 'terminal.donation.parked',
          params: { reason, ...(parkedCount !== undefined ? { parkedCount } : {}) },
        },
        subject: donationSubject(ctx),
        refs: repoRefs(ctx.row),
      };
    },
  },
  // `apps/api/src/deps/grants.ts` — free capacity handed out on the platform's
  // own margin. `decision`: exactly the row `admin:grants` exists to make
  // reviewable, and `grantedByUserId` is a required (never optional) field on
  // the writer, so `human` is not a default here, it is a fact.
  'grant.issued': {
    level: 'decision',
    actor: 'human',
    render: (ctx) => {
      const input = asRecord(ctx.row.input);
      const grantKind = str(input, 'grantKind') ?? 'unknown';
      const amount = scalarParam(input, 'amount');
      return {
        line: {
          key: 'terminal.grant.issued',
          params: { grantKind, ...(amount !== undefined ? { amount } : {}) },
        },
        subject: grantSubject(ctx),
        refs: {},
      };
    },
  },
  // The CAS that stops one. `revoked: false` (already stopped) never reaches
  // this renderer — that branch does not audit (`grants.ts` § "a benign no-op").
  'grant.revoked': {
    level: 'decision',
    actor: 'human',
    render: (ctx) => {
      const input = asRecord(ctx.row.input);
      const grantKind = str(input, 'grantKind') ?? 'unknown';
      return {
        line: { key: 'terminal.grant.revoked', params: { grantKind } },
        subject: grantSubject(ctx),
        refs: {},
      };
    },
  },
  // `apps/api/src/deps/goals.ts` — a board goal (a milestone or `type/epic`
  // issue) was retitled. `issue`: the closest `TerminalSubjectKind` to a board
  // goal, and the id GitHub actually understands. Routine metadata — `info`.
  'goal.renamed': {
    level: 'info',
    actor: 'human',
    render: (ctx) => {
      const input = asRecord(ctx.row.input);
      const output = asRecord(ctx.row.output);
      const goalId = idLike(input, 'goalId');
      const to = str(output, 'to') ?? str(input, 'requested') ?? 'unknown';
      const repo = str(input, 'repo');
      return {
        line: { key: 'terminal.goal.renamed', params: { to } },
        subject: subjectOrSession(ctx, 'issue', goalId, to),
        refs: repo !== undefined ? { repoFullName: repo } : repoRefs(ctx.row),
      };
    },
  },
  // `apps/api/src/deps/token-audit.ts` — a credential able to spend was minted.
  // "The first two rows an incident review looks for" (module header):
  // `decision`, not `info`.
  'token.created': {
    level: 'decision',
    actor: 'human',
    render: (ctx) => {
      const input = asRecord(ctx.row.input);
      const tokenId = idLike(input, 'tokenId');
      const reason = str(input, 'reason') ?? 'unknown';
      return {
        line: { key: 'terminal.token.created', params: { reason } },
        subject: subjectOrSession(ctx, 'key', tokenId, tokenId ?? 'token'),
        refs: {},
      };
    },
  },
  'token.revoked': {
    level: 'decision',
    actor: 'human',
    render: (ctx) => {
      const input = asRecord(ctx.row.input);
      const tokenId = idLike(input, 'tokenId');
      return {
        line: { key: 'terminal.token.revoked', params: {} },
        subject: subjectOrSession(ctx, 'key', tokenId, tokenId ?? 'token'),
        refs: {},
      };
    },
  },
  // A label move only — no reach changed. `info`, unlike its birth and death.
  'token.renamed': {
    level: 'info',
    actor: 'human',
    render: (ctx) => {
      const input = asRecord(ctx.row.input);
      const tokenId = idLike(input, 'tokenId');
      const name = str(input, 'name') ?? 'unknown';
      const previousName = str(input, 'previousName') ?? 'none';
      return {
        line: { key: 'terminal.token.renamed', params: { name, previousName } },
        subject: subjectOrSession(ctx, 'key', tokenId, name),
        refs: {},
      };
    },
  },
  // #5920: a refused step-up at the operator-token writer door. `decision`
  // (matches its birth/death siblings above) — the trace the issue wants is
  // an incident review reads, not a theme. `sessionAgeMs === -1` carries the
  // never-signed-in case so a maintainer can tell it from a stale cookie.
  'operator_token.stepup_refused': {
    level: 'decision',
    actor: 'human',
    render: (ctx) => {
      const input = asRecord(ctx.row.input);
      const sessionId = str(input, 'sessionId');
      const rawAge = input?.['sessionAgeMs'];
      const sessionAgeMs = typeof rawAge === 'number' ? rawAge : null;
      const age = sessionAgeMs === null ? 'never signed in' : `${sessionAgeMs}ms`;
      return {
        line: {
          key: 'terminal.operator_token.stepup_refused',
          params: { age },
        },
        // Pass `undefined` (not a literal `'session'`) when the row carries
        // no sessionId: `subjectOrSession` falls back to `ctx.row.sessionId`,
        // which is a session id (not a constant), and the test that pins
        // `subject.id === row.sessionId ⇒ kind === 'session'` holds.
        subject: subjectOrSession(ctx, 'session', sessionId, sessionId ?? 'session'),
        refs: {},
      };
    },
  },
  // #2270: the promoted account-plane verbs of this family (`./account-verb.ts`).
  ...ACCOUNT_VERBS_COMMERCE,
  // #6167: a webhook source's health transition (`./commerce-webhook-source.ts`).
  ...TERMINAL_RENDERERS_WEBHOOK_SOURCE_HEALTH,
  // #6679: the guidance sweep's attempt on one blocker (`./commerce-blocker-guidance.ts`).
  ...TERMINAL_RENDERERS_BLOCKER_GUIDANCE,
});
